import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Tag, Info } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { MOON } from "@/content/moon";
import { useLang } from "@/lib/lang";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Catalogue() {
  const { t, lang } = useLang();
  const [products, setProducts] = useState(null);
  const [category, setCategory] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/products", { params: { lang, category: category || undefined } })
      .then(({ data }) => setProducts(data))
      .catch((e) => { setError(formatApiErrorDetail(e.response?.data?.detail)); setProducts([]); });
  }, [lang, category]);

  const ctaFor = (p) => ({
    pathname: "/moon/recherche",
    search: `?produit=${encodeURIComponent(p.name.replace(/^EXEMPLE — /, ""))}&type=${p.mode === "location" ? "location" : "achat"}`,
  });

  return (
    <main data-testid="catalogue-page">
      <Seo
        title={lang === "fr"
          ? "Catalogue d'équipements — MOON Supply | Export Monde Prestige Inc."
          : "Equipment catalogue — MOON Supply | Export Monde Prestige Inc."}
        description={lang === "fr"
          ? "Parcourez les équipements d'achat et de location coordonnés par MOON Supply. Disponibilité à confirmer — demandez un devis."
          : "Browse purchase and rental equipment coordinated by MOON Supply. Availability to be confirmed — request a quote."}
        path="/catalogue"
      />
      <section className="bg-gradient-to-b from-ice via-white to-white pb-14 pt-[152px] md:pt-[200px]">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">MOON Supply</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-navy-950 md:text-6xl" data-testid="catalogue-title">
              {lang === "fr" ? "Catalogue d'équipements" : "Equipment catalogue"}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-graphite md:text-lg">
              {lang === "fr"
                ? "Achat ou location : chaque fiche est coordonnée par notre équipe. La disponibilité est toujours « à confirmer » jusqu'à vérification réelle auprès du fournisseur."
                : "Purchase or rental: every listing is coordinated by our team. Availability is always « to be confirmed » until verified with the supplier."}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper py-14 md:py-20">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-2" role="group" aria-label={lang === "fr" ? "Catégories" : "Categories"}>
              <button
                onClick={() => setCategory("")} data-testid="catalogue-filter-all"
                className={`rounded-full border px-4 py-2 text-[12.5px] font-semibold transition-colors ${category === "" ? "border-electric bg-electric text-white" : "border-slate-200 bg-white text-graphite hover:border-electric hover:text-electric"}`}
              >
                {lang === "fr" ? "Toutes" : "All"}
              </button>
              {MOON.categories.map((c) => (
                <button
                  key={c.slug} onClick={() => setCategory(c.slug)} data-testid={`catalogue-filter-${c.slug}`}
                  className={`rounded-full border px-4 py-2 text-[12.5px] font-semibold transition-colors ${category === c.slug ? "border-electric bg-electric text-white" : "border-slate-200 bg-white text-graphite hover:border-electric hover:text-electric"}`}
                >
                  {t(c.label)}
                </button>
              ))}
            </div>
          </Reveal>

          {error && <p role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</p>}
          {!products && <p className="mt-6 text-sm text-steel">{lang === "fr" ? "Chargement…" : "Loading…"}</p>}
          {products && products.length === 0 && (
            <p className="mt-6 rounded-xl border border-sky-200 bg-ice p-6 text-sm text-navy-900" data-testid="catalogue-empty">
              {lang === "fr"
                ? "Aucun produit publié dans cette catégorie pour le moment. Décrivez votre besoin : MOON le recherche pour vous."
                : "No products published in this category yet. Describe your need: MOON finds it for you."}
              <Link to="/moon/recherche" className="ml-2 font-bold text-electric underline">{t(MOON.ctaSearch)}</Link>
            </p>
          )}

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalogue-grid">
            {(products || []).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.04} className="h-full">
                <article className="flex h-full flex-col rounded-2xl border border-slate-200/90 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-lift" data-testid={`catalogue-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <span className="rounded-full bg-badge px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest2 text-sky-700">
                      {p.mode === "achat_location" ? (lang === "fr" ? "Achat ou location" : "Purchase or rental") : t(MOON.types[p.mode] || { fr: p.mode, en: p.mode })}
                    </span>
                    {p.demo && (
                      <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-700" data-testid={`catalogue-demo-badge-${p.id}`}>
                        {lang === "fr" ? "Exemple" : "Example"}
                      </span>
                    )}
                  </div>
                  <h2 className="mt-4 font-display text-lg font-bold leading-snug text-navy-950">{p.name}</h2>
                  <p className="mt-2 text-[13px] leading-relaxed text-graphite">{p.description}</p>
                  <ul className="mt-4 space-y-1.5 text-[12.5px] text-graphite">
                    {p.manufacturer && <li className="flex items-center gap-2"><Tag size={13} className="shrink-0 text-electric" /> {p.manufacturer}{p.part_number ? ` · ${p.part_number}` : ""}</li>}
                    {p.supplier_location && <li className="flex items-center gap-2"><MapPin size={13} className="shrink-0 text-electric" /> {lang === "fr" ? "Fournisseur" : "Supplier"} : {p.supplier_location}</li>}
                  </ul>
                  <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                    <span className="rounded-full bg-ice px-3 py-1 text-[11px] font-bold text-sky-700">{lang === "fr" ? "Disponibilité à confirmer" : "Availability to be confirmed"}</span>
                    <span className="text-sm font-bold text-navy-950">{p.price || (lang === "fr" ? "Sur devis" : "Quote on request")}</span>
                  </div>
                  <Link
                    to={ctaFor(p)} state={{ product: p.name.replace(/^EXEMPLE — /, ""), mode: p.mode }}
                    data-testid={`catalogue-quote-button-${p.id}`}
                    className="group mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-electric px-6 py-3 text-[13px] font-bold text-white transition-colors hover:bg-electric-dark"
                  >
                    {t(MOON.ctaQuote)} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="mt-10 flex items-start gap-3 rounded-2xl border border-sky-200 bg-ice p-5 text-[13px] leading-relaxed text-navy-900">
              <Info size={18} className="mt-0.5 shrink-0 text-electric" />
              {t(MOON.notice)}
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
